import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, CheckCircle2, Info, Loader2, Mail, PlugZap, Save } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { cn, formatarDataHora, paraBooleano } from '@/lib/utils';
import type { ConexaoSmtp, SituacaoSmtp } from '@/features/notificacoes/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';

const esquema = z.object({
  host: z.string().min(1, 'Informe o servidor SMTP.'),
  porta: z.coerce.number().int().min(1).max(65535),
  seguro: z.boolean(),
  usuario: z.string().optional(),
  senha: z.string().optional(),
  remetenteNome: z.string().min(1, 'Informe o nome exibido como remetente.'),
  remetenteEmail: z.string().email('Informe um e-mail remetente válido.'),
  destinoTeste: z.string().email('Informe um destinatário válido.').or(z.literal('')),
  ativo: z.boolean(),
});

type Formulario = z.infer<typeof esquema>;

interface ResultadoTeste {
  ok: boolean;
  mensagem: string;
}

export function PaginaSmtp() {
  const queryClient = useQueryClient();
  const [resultado, setResultado] = React.useState<ResultadoTeste | null>(null);

  const { data: conexao, isPending } = useQuery({
    queryKey: ['smtp-conexao'],
    queryFn: () => api.get<ConexaoSmtp>('/smtp/conexao'),
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    values: {
      host: conexao?.host ?? '',
      porta: conexao?.porta ?? 587,
      seguro: conexao?.seguro ?? false,
      usuario: conexao?.usuario ?? '',
      senha: '',
      remetenteNome: conexao?.remetenteNome ?? 'Setor de Regulação',
      remetenteEmail: conexao?.remetenteEmail ?? '',
      destinoTeste: '',
      ativo: conexao?.ativo ?? false,
    },
  });

  const testar = useMutation({
    mutationFn: (dados: Formulario) =>
      api.post<ResultadoTeste>('/smtp/conexao/testar', {
        host: dados.host,
        porta: dados.porta,
        seguro: dados.seguro,
        usuario: dados.usuario || undefined,
        senha: dados.senha || undefined,
        remetenteNome: dados.remetenteNome,
        remetenteEmail: dados.remetenteEmail,
        destinoTeste: dados.destinoTeste || undefined,
      }),
    onSuccess: async (r) => {
      setResultado(r);
      await queryClient.invalidateQueries({ queryKey: ['smtp-conexao'] });
    },
    onError: (erro) =>
      setResultado({
        ok: false,
        mensagem: erro instanceof ErroApi ? erro.mensagemUsuario : 'Falha ao testar.',
      }),
  });

  // O que vale de fato agora — a tela precisa poder contradizer o "teste
  // aceito" quando a configuração está salva mas desativada.
  const situacao = useQuery({
    queryKey: ['smtp-situacao'],
    queryFn: () => api.get<SituacaoSmtp>('/smtp/situacao'),
  });

  const salvar = useMutation({
    mutationFn: ({ destinoTeste: _, ...dados }: Formulario) =>
      api.patch<ConexaoSmtp>('/smtp/conexao', { ...dados, senha: dados.senha || undefined }),
    onSuccess: async () => {
      toast.success('Configuração salva.');
      formulario.setValue('senha', '');
      await queryClient.invalidateQueries({ queryKey: ['smtp-conexao'] });
      await queryClient.invalidateQueries({ queryKey: ['smtp-situacao'] });
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.'),
  });

  const aoSalvar = formulario.handleSubmit((d) => salvar.mutate(d));
  const aoTestar = formulario.handleSubmit((d) => testar.mutate(d));

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-80" />
        <Esqueleto className="h-96 w-full max-w-2xl" />
      </div>
    );
  }

  const ativo = formulario.watch('ativo');
  // O `watch` devolve o que está no campo, e um input numérico entrega texto:
  // comparar com o número direto nunca casaria.
  const porta = Number(formulario.watch('porta'));

  return (
    <div className="max-w-2xl">
      <CabecalhoPagina
        titulo="Servidor de e-mail"
        descricao="Por onde saem o aviso de agendamento ao paciente, a cópia à UBS e a redefinição de senha."
      />

      {situacao.data && (
        <div
          role={situacao.data.configuracaoIgnorada ? 'alert' : undefined}
          className={cn(
            'mb-4 flex items-start gap-2 rounded-md border p-3 text-sm',
            situacao.data.configuracaoIgnorada
              ? 'border-status-pendencia-borda bg-status-pendencia-fundo text-status-pendencia-texto'
              : 'border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto',
          )}
        >
          {situacao.data.configuracaoIgnorada ? (
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          ) : (
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          )}
          <div>
            <p className="font-medium">
              {situacao.data.origem === 'banco'
                ? `As mensagens estão saindo por ${situacao.data.host}.`
                : `As mensagens estão saindo pelo servidor do ambiente (${situacao.data.host}).`}
            </p>
            <p className="mt-0.5">
              {situacao.data.configuracaoIgnorada ? (
                <>
                  Há um servidor configurado aqui que <strong>não está em uso</strong>: a situação
                  abaixo está como desativada. O botão &quot;Testar&quot; envia com o que está no
                  formulário e passa mesmo assim — quem decide o envio real é a situação.
                </>
              ) : (
                <>
                  Remetente: {situacao.data.remetente}. Sem configuração ativa, valem as variáveis
                  do ambiente — é o que mantém o sistema enviando durante a implantação.
                </>
              )}
            </p>
          </div>
        </div>
      )}

      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo className="flex items-center gap-2">
            <Mail className="size-4" aria-hidden="true" />
            Dados do servidor
          </CartaoTitulo>
          <CartaoDescricao>
            {conexao?.ultimoTesteEm ? (
              <span className="flex flex-wrap items-center gap-1.5">
                Último teste em {formatarDataHora(conexao.ultimoTesteEm)}
                <Etiqueta tom={conexao.ultimoTesteOk ? 'sucesso' : 'perigo'}>
                  {conexao.ultimoTesteOk ? 'aceito' : 'com problema'}
                </Etiqueta>
              </span>
            ) : (
              'O servidor ainda não foi testado.'
            )}
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo>
          <form onSubmit={(e) => void aoSalvar(e)} noValidate className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-[1fr_140px]">
              <Campo
                id="smtp-host"
                rotulo="Servidor"
                obrigatorio
                erro={formulario.formState.errors.host?.message}
              >
                <Entrada placeholder="smtp.prefeitura.gov.br" {...formulario.register('host')} />
              </Campo>

              <Campo
                id="smtp-porta"
                rotulo="Porta"
                obrigatorio
                erro={formulario.formState.errors.porta?.message}
              >
                <Entrada type="number" inputMode="numeric" {...formulario.register('porta')} />
              </Campo>
            </div>

            <Campo
              id="smtp-seguro"
              rotulo="Conexão"
              ajuda="A 465 exige TLS direto; a 587 negocia STARTTLS sozinha."
            >
              <Selecao {...formulario.register('seguro', { setValueAs: paraBooleano })}>
                <option value="false">STARTTLS ou sem criptografia (587, 25, 1025)</option>
                <option value="true">TLS direto (465)</option>
              </Selecao>
            </Campo>

            {porta === 465 && !formulario.watch('seguro') && (
              <p className="text-alerta flex items-start gap-1.5 text-xs" role="status">
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />A porta 465
                quase sempre exige TLS direto. Do jeito atual a conexão tende a travar até o tempo
                limite.
              </p>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="smtp-usuario"
                rotulo="Usuário"
                ajuda="Deixe vazio se o servidor não exigir autenticação."
              >
                <Entrada autoComplete="off" {...formulario.register('usuario')} />
              </Campo>

              <Campo
                id="smtp-senha"
                rotulo="Senha"
                ajuda={
                  conexao?.temSenha
                    ? 'Já existe uma senha salva. Deixe em branco para mantê-la.'
                    : 'Nenhuma senha salva ainda.'
                }
              >
                <Entrada
                  type="password"
                  autoComplete="new-password"
                  placeholder={conexao?.temSenha ? '••••••••' : ''}
                  {...formulario.register('senha')}
                />
              </Campo>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="smtp-remetente-nome"
                rotulo="Nome do remetente"
                obrigatorio
                erro={formulario.formState.errors.remetenteNome?.message}
              >
                <Entrada {...formulario.register('remetenteNome')} />
              </Campo>

              <Campo
                id="smtp-remetente-email"
                rotulo="E-mail do remetente"
                obrigatorio
                ajuda="Precisa ser um endereço que o servidor autorize a enviar."
                erro={formulario.formState.errors.remetenteEmail?.message}
              >
                <Entrada
                  type="email"
                  placeholder="nao-responda@prefeitura.gov.br"
                  {...formulario.register('remetenteEmail')}
                />
              </Campo>
            </div>

            <Campo
              id="smtp-ativo"
              rotulo="Situação"
              ajuda="Desativado, o envio continua pelo servidor definido no ambiente."
            >
              <Selecao {...formulario.register('ativo', { setValueAs: paraBooleano })}>
                <option value="false">Desativado (usa o servidor do ambiente)</option>
                <option value="true">Ativo (usa os dados acima)</option>
              </Selecao>
            </Campo>

            {ativo && !conexao?.ultimoTesteOk && (
              <p className="text-alerta flex items-start gap-1.5 text-xs" role="status">
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                Teste antes de ativar: um servidor recusado deixa cada paciente agendado sem aviso,
                e a falha só aparece na lista de notificações.
              </p>
            )}

            <Campo
              id="smtp-destino-teste"
              rotulo="Enviar mensagem de teste para"
              ajuda="Opcional. Um servidor costuma aceitar a conexão e recusar o envio depois — só o envio de verdade revela isso."
              erro={formulario.formState.errors.destinoTeste?.message}
            >
              <Entrada
                type="email"
                placeholder="seu.email@prefeitura.gov.br"
                {...formulario.register('destinoTeste')}
              />
            </Campo>

            {resultado && (
              <div
                role="status"
                className={cn(
                  'flex items-start gap-2 rounded-md border p-3 text-sm',
                  resultado.ok
                    ? 'border-status-agendado-borda bg-status-agendado-fundo text-status-agendado-texto'
                    : 'border-status-devolvido-borda bg-status-devolvido-fundo text-status-devolvido-texto',
                )}
              >
                {resultado.ok ? (
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                ) : (
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                )}
                <span className="min-w-0 break-words">
                  {resultado.mensagem}
                  {/* O momento de maior confusão é este: verde na tela, e o
                      envio real continuando por outro servidor. */}
                  {resultado.ok && !ativo && (
                    <strong className="mt-1 block font-medium">
                      O teste usou os dados deste formulário. Como a situação abaixo está
                      desativada, os envios do sistema continuam saindo pelo servidor do ambiente —
                      ative e salve para valer.
                    </strong>
                  )}
                </span>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Botao
                type="button"
                variante="contorno"
                onClick={() => void aoTestar()}
                disabled={testar.isPending}
              >
                {testar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <PlugZap aria-hidden="true" />
                )}
                {testar.isPending ? 'Testando…' : 'Testar'}
              </Botao>

              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Save aria-hidden="true" />
                )}
                Salvar
              </Botao>
            </div>
          </form>
        </CartaoConteudo>
      </Cartao>
    </div>
  );
}
